import React from 'react';
import { useHistory } from 'react-router-dom';
import ViewerActions from '../actions/ViewerActions';
import ViewerStore from '../stores/ViewerStore';
import FacilityStore from '../stores/FacilityStore';

/** Mounted with FacilityApp, so hiding a viewer panel cannot remove navigation protection. */
export default function KnowledgeViewNavigationGuard() {
    const history = useHistory();
    React.useEffect(() => {
        const leavesView = (url: URL) => {
            // During POP, window.location already points at the destination. Compare
            // with the still-active router/store state instead of the browser URL.
            return url.origin !== window.location.origin || url.pathname !== history.location.pathname ||
                url.searchParams.get('facilityId') !== FacilityStore.getSelectedFacility()?.ID ||
                url.searchParams.get('kvId') !== ViewerStore.getSelectedKnowledgeView()?.ID;
        };
        const unblock = history.block((location, action) => {
            const url = new URL(location.pathname + location.search, window.location.origin);
            if (!leavesView(url)) return;
            if (ViewerActions.checkForKnowledgeViewChanges(() => {
                if (action === 'REPLACE') history.replace(location);
                else history.push(location);
            })) return false;
        });
        const beforeUnload = (event: BeforeUnloadEvent) => {
            if (ViewerStore.isEditingKnowledgeView() && ViewerStore.isKnowledgeViewDirty()) {
                event.preventDefault();
                event.returnValue = '';
            }
        };
        const followLink = (event: MouseEvent) => {
            if (event.defaultPrevented || event.button !== 0 || event.ctrlKey || event.metaKey || event.shiftKey || event.altKey) return;
            const anchor = (event.target as Element)?.closest<HTMLAnchorElement>('a[href]');
            if (!anchor || anchor.download || (anchor.target && anchor.target !== '_self')) return;
            const url = new URL(anchor.href, window.location.href);
            if (!['http:', 'https:'].includes(url.protocol) || !leavesView(url)) return;
            if (ViewerActions.checkForKnowledgeViewChanges(() => { window.location.assign(url.href); })) {
                event.preventDefault();
                event.stopPropagation();
            }
        };
        document.addEventListener('click', followLink, true);
        window.addEventListener('beforeunload', beforeUnload);
        return () => {
            unblock();
            document.removeEventListener('click', followLink, true);
            window.removeEventListener('beforeunload', beforeUnload);
        };
    }, [history]);
    return null;
}
